import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { List, Briefcase, CheckSquare, User, Bell, LogOut, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/app/NotificationsBell";

/**
 * BankStaffShell — Mobile-first shell for the Bank Staff app.
 *
 *  ┌──────────────────────────────────────────────────────────┐
 *  │ [S-mark]  Sakthi                    [🔔]  [avatar]        │  ← 64px sticky
 *  │           BANK STAFF                                       │
 *  ├──────────────────────────────────────────────────────────┤
 *  │ DISTRICT · TENKASI                          [+ Add Cust.] │
 *  │ Wanted List                                                │
 *  │                                                            │
 *  │ ── scrollable page content ──                              │
 *  │                                                            │
 *  ├──────────────────────────────────────────────────────────┤
 *  │  [📋]     [💼]        [✅]        [👤]                    │  ← 72px fixed
 *  │  Wanted   Taken       OK 30d      Profile                  │
 *  └──────────────────────────────────────────────────────────┘
 */

const TABS = [
  { key: "refer",   label: "Refer",    sub: "Earn 1%", icon: Gift },
  { key: "wanted",  label: "Wanted",   sub: "List",    icon: List },
  { key: "taken",   label: "Taken",    sub: "24h SLA", icon: Briefcase },
  { key: "ok",      label: "OK Cases", sub: "30d",     icon: CheckSquare },
  { key: "profile", label: "Profile",  sub: "Account", icon: User },
];

export default function BankStaffShell({ active, onNav, pageTitle, headerAction, hasOverdue = false, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };
  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0E1E] text-white overflow-x-hidden">
      {/* Top header — sticky 64px */}
      <header
        data-testid="bank-header"
        className="h-16 sticky top-0 z-40 bg-[#0A0E1E] border-b border-[#1E2742] px-4 flex items-center gap-3"
      >
        <Link to="/bank" className="flex items-center gap-2.5 min-w-0">
          <img
            src="/logo-mark-round.png"
            alt="Sakthi"
            className="w-10 h-10 rounded-lg bg-white p-0.5 border border-[#C5A028]/40 object-contain shrink-0"
          />
          <div className="min-w-0 leading-none">
            <div className="font-heading text-white text-lg leading-tight truncate">Sakthi</div>
            <div className="text-[11px] tracking-[0.2em] text-[#C5A028] uppercase font-bold mt-0.5">Bank Staff</div>
          </div>
        </Link>

        <div className="ml-auto flex items-center gap-2 shrink-0">
          <NotificationsBell/>
          <div className="relative group">
            <div
              data-testid="bank-avatar"
              title={`${user?.name || ""}\n${user?.phone || ""}\nLogout`}
              className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C5A028] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-bold text-xs cursor-pointer border border-[#C5A028]/60"
              onClick={doLogout}
            >
              {initials}
            </div>
          </div>
        </div>
      </header>

      {/* Page title / district / header action */}
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="text-[11px] font-bold text-[#C5A028] uppercase"
            style={{ letterSpacing: "0.25em" }}
            data-testid="bank-district"
          >
            District · {user?.district || "—"}
          </div>
          <h1 className="font-heading text-white text-2xl sm:text-3xl mt-1 leading-tight break-words">{pageTitle}</h1>
        </div>
        {headerAction ? <div className="shrink-0 pt-1">{headerAction}</div> : null}
      </div>

      {/* Main content — leave room for bottom nav */}
      <main className="flex-1 px-4 pb-28 pt-2 min-w-0">
        {children}
      </main>

      {/* Bottom nav — fixed 72px */}
      <nav
        data-testid="bank-bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-40 h-[72px] bg-[#0A0E1E] border-t border-[#1E2742] flex items-stretch justify-around px-2 pb-safe"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {TABS.map(t => {
          const isActive = active === t.key;
          const showDot = t.key === "taken" && hasOverdue;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onNav(t.key)}
              data-testid={`bottom-nav-${t.key}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 mx-1 my-2 rounded-xl transition-all active:scale-95 relative ${
                isActive
                  ? "bg-[#FF8C00] text-slate-900 shadow-lg shadow-orange-500/20"
                  : "text-[#6B7280] hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="w-5 h-5"/>
              <span className={`text-[10px] font-bold leading-none ${isActive ? "text-slate-900" : ""}`}>{t.label}</span>
              <span className={`text-[8px] uppercase tracking-widest leading-none opacity-70`}>{t.sub}</span>
              {showDot && (
                <span className="absolute top-1.5 right-4 w-2 h-2 rounded-full bg-red-500 border border-slate-900" data-testid="bottom-nav-overdue-dot"/>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom footer text */}
      <div className="fixed bottom-[76px] left-0 right-0 text-center text-[9px] text-slate-600 uppercase tracking-widest pointer-events-none z-30 sm:hidden">
        www.sakthiloanconsultancy.com
      </div>
    </div>
  );
}

/** Small header button — used for "Add Customer" or "Boost" pill. */
export function HeaderPill({ children, onClick, testId, tone = "primary" }) {
  const cls = tone === "primary"
    ? "bg-[#FF8C00] hover:bg-[#e77e00] text-slate-900 shadow-lg shadow-orange-500/20"
    : "bg-slate-800 hover:bg-slate-700 text-white border border-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-11 px-4 rounded-full font-bold text-sm inline-flex items-center gap-2 active:scale-95 transition ${cls}`}
    >
      {children}
    </button>
  );
}
